import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCustomerChargeTerms } from '../../../services/chargeControls';
import { useWizardCustomerId } from '../wizardCustomer';

/** How often the default interest is charged; CUSTOM_DAYS uses the interval in days. */
export const DEFAULT_CHARGE_FREQUENCY_LABELS: Record<string, string> = {
  MONTHLY: 'Monthly',
  WEEKLY: 'Weekly',
  CUSTOM_DAYS: 'Custom (every N days)',
  ONCE_OFF: 'Once, at default',
};

/** "monthly", "every 15 days", ... for summaries. Empty frequency means monthly. */
export const describeChargeFrequency = (frequency?: string, intervalDays?: number): string => {
  if (frequency === 'CUSTOM_DAYS') return intervalDays ? `every ${intervalDays} days` : 'custom (days not set)';
  if (frequency === 'WEEKLY') return 'weekly';
  if (frequency === 'ONCE_OFF') return 'once at default';
  return 'monthly';
};

const toOptionalNumber = (v: unknown) => (v === '' || v === null || v === undefined ? undefined : Number(v));

/**
 * What the loan charges if the client defaults, agreed per loan. All optional: when left blank the
 * loan's normal interest rate is used, charged monthly, with no grace period - and the terms can be
 * set later from the loan's pending charges.
 */
const DefaultTermsFields: React.FC = () => {
  const { register, watch, getValues, setValue, formState: { errors } } = useFormContext();
  const loanErrors: any = (errors as any)?.loan || {};
  const isCustom = watch('loan.defaultChargeFrequency') === 'CUSTOM_DAYS';

  // A returning customer with personal charge terms (#38): they prefill blank default terms, once
  const { data: customerTerms } = useCustomerChargeTerms(useWizardCustomerId());
  const prefilled = useRef(false);
  const [fromCustomer, setFromCustomer] = useState(false);
  useEffect(() => {
    if (!customerTerms || prefilled.current) return;
    prefilled.current = true;
    const blank = ['defaultInterestRate', 'defaultChargeFrequency', 'defaultChargeIntervalDays', 'defaultGraceDays']
      .every(f => { const v = getValues(`loan.${f}`); return v === undefined || v === null || v === ''; });
    if (!blank) return;
    if (customerTerms.defaultInterestRate != null) setValue('loan.defaultInterestRate', customerTerms.defaultInterestRate);
    if (customerTerms.defaultChargeFrequency) setValue('loan.defaultChargeFrequency', customerTerms.defaultChargeFrequency);
    if (customerTerms.defaultChargeIntervalDays != null) setValue('loan.defaultChargeIntervalDays', customerTerms.defaultChargeIntervalDays);
    if (customerTerms.defaultGraceDays != null) setValue('loan.defaultGraceDays', customerTerms.defaultGraceDays);
    setFromCustomer(true);
  }, [customerTerms, getValues, setValue]);

  return (
    <div className="col-12">
      <div className="card border">
        <div className="card-body">
          <h6 className="mb-1">Default terms</h6>
          {fromCustomer && <div className="alert alert-info small py-1 px-2 mb-2">Prefilled from this customer's personal charge terms. You can change them for this loan.</div>}
          <p className="text-muted small mb-3">
            Charged if the client defaults: on the principal while they owe more than it, and on the
            outstanding balance once that falls below. Leave blank to use the loan's interest rate,
            monthly, with no grace period. These can also be set later from the loan's pending charges.
          </p>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label htmlFor="defaultInterestRate" className="form-label">Default rate (%)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                id="defaultInterestRate"
                className={`form-control ${loanErrors.defaultInterestRate ? 'is-invalid' : ''}`}
                placeholder="e.g. 5"
                {...register('loan.defaultInterestRate', { setValueAs: toOptionalNumber })}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="defaultChargeFrequency" className="form-label">Charged</label>
              <select
                id="defaultChargeFrequency"
                className="form-select"
                {...register('loan.defaultChargeFrequency', { setValueAs: v => (v === '' ? undefined : v) })}
              >
                <option value="">Monthly (default)</option>
                {Object.entries(DEFAULT_CHARGE_FREQUENCY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {isCustom && (
              <div className="col-md-3">
                <label htmlFor="defaultChargeIntervalDays" className="form-label">Every (days)</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  id="defaultChargeIntervalDays"
                  className={`form-control ${loanErrors.defaultChargeIntervalDays ? 'is-invalid' : ''}`}
                  placeholder="e.g. 15"
                  {...register('loan.defaultChargeIntervalDays', { setValueAs: toOptionalNumber })}
                />
              </div>
            )}
            <div className="col-md-3">
              <label htmlFor="defaultGraceDays" className="form-label">Grace period (days)</label>
              <input
                type="number"
                min={0}
                step={1}
                id="defaultGraceDays"
                className={`form-control ${loanErrors.defaultGraceDays ? 'is-invalid' : ''}`}
                placeholder="0"
                {...register('loan.defaultGraceDays', { setValueAs: toOptionalNumber })}
              />
            </div>
          </div>
          {/* Errors sit under the row, so a message never pushes one input out of line */}
          {['defaultInterestRate', 'defaultChargeIntervalDays', 'defaultGraceDays']
            .filter(field => loanErrors[field])
            .map(field => (
              <div key={field} className="text-danger small mt-2">{loanErrors[field].message as string}</div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultTermsFields;
